import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { ClickOutsideHandler } from '@core/services';
import { Subject, timer } from 'rxjs';
import { TAutoNumericOptions } from 'src/app/core/helpers/create-autonumeric';
import { trimNumericInput } from 'src/app/core/helpers/trim-numeric-input';
import { TPoint } from '../../points/points.model';
import { PointsService } from '../../points/points.service';
import { getDecimalPlacesPlaceholder } from '../custom-field-numbers/utils/getDecimalPlacesPlaceholder';
import { EIntegrationStatus } from '../custom-fields.model';
import { checkClearShortcut } from '../utils/check-clear-shortcut';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';

@Component({
  selector: 'pp-custom-field-cost',
  templateUrl: './custom-field-cost.component.html',
  styleUrls: ['./custom-field-cost.component.scss'],
  standalone: false,
})
export class CustomFieldCostComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('costInput', { static: false }) costInputElement: ElementRef;
  @ViewChild('customFieldElement', { static: false }) customFieldElement: ElementRef;

  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppFieldCurrencySymbol: string;
  @Input() ppFieldCurrencyCode: string;
  @Input() ppFieldValue: string;
  @Input() ppNew: boolean;
  @Input() ppCanEdit: boolean;
  @Input() ppIntegrationStatus: EIntegrationStatus;
  @Input() ppDecimalPlaces: number;
  @Input() ppCurrencyName: string = '';
  @Input() ppIsLocked: boolean;

  @Output() ppUpdate = new EventEmitter<string>();

  value: string;
  autonumericOptions: TAutoNumericOptions;
  placeholder: string;
  currencyTooltip: string;
  EIconPath = EIconPath;

  updating = false;
  success = false;
  error = false;
  focused = false;
  EIntegrationStatus = EIntegrationStatus;
  private readonly destroy$ = new Subject<void>();
  private clickOutsideHandler: ClickOutsideHandler;
  private removeClickListenerTimerMs = 100;

  constructor(
    private pointsService: PointsService,
    private ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.setAutonumericOptions();
    this.setPlaceholder();
    this.setCurrencyTooltip();

    if (changes.ppPointId) {
      const point: TPoint = this.pointsService.findPoint(this.ppFieldId);

      if (point) {
        this.ppFieldValue = point.customFieldsMap[this.ppFieldId]?.value;
      }
    }
  }

  ngAfterViewInit() {
    this.clickOutsideHandler = new ClickOutsideHandler(
      this.customFieldElement.nativeElement,
      this.destroy$,
    );

    this.clickOutsideHandler.caught$.subscribe((event) => {
      this.onClickOutside(event);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  clearCustomField(): void {
    this.costInputElement.nativeElement.blur();
    this.ppFieldValue = null;
    this.updateField(null);
  }

  updateField(value: string): void {
    if (!this.ppIsLocked) {
      const fieldValue = value;
      this.ppFieldValue = fieldValue;
      const newValue = trimNumericInput(fieldValue);

      this.ppUpdate.emit(newValue);
    }
  }

  onBlur(): void {
    this.focused = false;
    this.ngZone.runOutsideAngular(() => {
      timer(this.removeClickListenerTimerMs).subscribe(() => {
        this.clickOutsideHandler.disable();
      });
    });
  }

  onFocus(): void {
    this.focused = true;

    this.clickOutsideHandler.enable();
  }

  blurInput(event: Event): void {
    const target = event.target as HTMLElement;

    target.blur();
  }

  onKeyDown(event: KeyboardEvent): void {
    checkClearShortcut(event, () => this.clearCustomField());
  }

  private onClickOutside(event: MouseEvent): void {
    event.stopImmediatePropagation();
    this.costInputElement.nativeElement.blur();
  }

  private setAutonumericOptions(): void {
    this.autonumericOptions = {
      formulaMode: true,
      watchExternalChanges: true,
      decimalPlaces: this.ppDecimalPlaces,
      modifyValueOnWheel: false,
    };
  }

  private setPlaceholder(): void {
    this.placeholder = getDecimalPlacesPlaceholder(this.ppDecimalPlaces);
  }

  private setCurrencyTooltip(): void {
    let tooltip = '';

    if (this.ppCurrencyName) {
      tooltip = this.ppCurrencyName;

      if (this.ppFieldCurrencySymbol) {
        tooltip += ` (${this.ppFieldCurrencySymbol})`;
      }
    } else if (this.ppFieldCurrencySymbol) {
      tooltip = this.ppFieldCurrencySymbol;
    } else {
      tooltip = this.ppCurrencyName;
    }

    this.currencyTooltip = tooltip;
  }
}
